import React, { useEffect, useState } from "react";
import axios from "axios";
import { ROLE_OPTIONS, isCompanyAdmin, isBUAdmin, convertLegacyRole, convertLegacyRoleWithContext, getEffectiveRole } from "../../constants/roles";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
  useToast,
  Avatar,
  Icon,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Text,
  Tr,
  Td,
  HStack,
  SimpleGrid,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure as useAlertDisclosure,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Divider,
  FormHelperText,
  InputGroup,
  InputRightAddon,
  Menu,
  MenuButton,
  MenuList,
  MenuOptionGroup,
  MenuItemOption,
  Switch,
} from "@chakra-ui/react";
import { FiSave, FiUpload, FiEdit, FiTrash2, FiChevronDown, FiMail, FiUsers, FiBriefcase } from "react-icons/fi";
import { lookupUserByEmail } from "../../api/authApi";
import { useAuth } from "../../context/AuthContext";
import { useBusinessUnit } from "../../context/BusinessUnitContext";
import { useTranslation } from "react-i18next";
import {
  PageHeader,
  Panel,
  DataTable,
  StatusBadge,
  MiniIconButton,
  EmptyState,
} from "../../components/Portal/ui";

// Shared compact button props matching the dense template scale.
const primaryBtnProps = {
  variant: "primary",
  size: "sm",
  h: "31px",
  px: "14px",
  fontSize: "12px",
};

const secondaryBtnProps = {
  variant: "secondary",
  size: "sm",
  h: "31px",
  px: "14px",
  fontSize: "12px",
};

// Section header (label/description-left column) — mirrors ProfileSettings.
const SectionHeading = ({ title, description }) => (
  <Box pr={{ base: 0, md: 8 }} minW={{ md: "220px" }}>
    <Heading
      as="h3"
      fontSize="14px"
      letterSpacing="-0.02em"
      color="portal.text"
      sx={{ marginBottom: "6px !important" }}
    >
      {title}
    </Heading>
    {description && (
      <Text fontSize="12px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
        {description}
      </Text>
    )}
  </Box>
);

// White section card — Panel with body padding.
const SectionCard = ({ children, ...rest }) => (
  <Panel {...rest}>
    <Box p="20px">{children}</Box>
  </Panel>
);

const ConfigRow = ({ label, children }) => (
  <Flex
    align="center"
    gap="12px"
    py="10px"
    borderBottomWidth="1px"
    borderColor="#edf2f5"
    _last={{ borderBottomWidth: 0 }}
  >
    <Text
      fontSize="11px"
      fontWeight="800"
      textTransform="uppercase"
      letterSpacing="0.04em"
      color="portal.muted"
      minW="160px"
      sx={{ marginBottom: "0 !important" }}
    >
      {label}
    </Text>
    <Box fontSize="13px" color="portal.text" fontWeight="600">
      {children}
    </Box>
  </Flex>
);

const CompanySettingsTab = () => {
  const { user } = useAuth();
  const { businessUnits, effectiveRole, isLoaded } = useBusinessUnit();
  const isCompanyAdminUser = isCompanyAdmin(convertLegacyRoleWithContext(user?.role, businessUnits.length > 0));
  const { t } = useTranslation("portal");
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState("");
  const [form, setForm] = useState({
    company_name: "",
    manager_name: "",
    billing_manager_name: "",
    billing_manager_email: "",
    website: "",
  });
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    // Only fetch company data after the BusinessUnitContext has finished loading
    if (!isLoaded) return;

    const fetchData = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BASE_API_URL}/auth/company`);
        setForm({
          company_name: res.data.company_name || "",
          manager_name: res.data.manager_name || "",
          billing_manager_name: res.data.billing_manager_name || "",
          billing_manager_email: res.data.billing_manager_email || "",
          website: res.data.website || "",
        });
        setLogoPreview(res.data.logo_url || "");
      } catch (err) {
        toast({ title: t("settings.company.toasts.loadFailed"), status: "error" });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isLoaded, toast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setForm((prev) => ({ ...prev, logo: file }));
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (value !== undefined && value !== null) data.append(key, value);
      });
      await axios.patch(`${process.env.REACT_APP_BASE_API_URL}/auth/company`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast({ title: t("settings.company.toasts.saveSuccess"), status: "success" });
      onClose();
    } catch (err) {
      toast({ title: t("settings.company.toasts.saveFailed"), status: "error" });
    } finally {
      setSaving(false);
    }
  };

  // Additional security check - only company admins can access this component
  if (!isCompanyAdminUser) {
    return (
      <SectionCard>
        <EmptyState
          icon={<FiBriefcase />}
          title={t("settings.company.accessDeniedTitle", "Access denied")}
          hint={t("settings.company.accessDeniedDescription")}
        />
      </SectionCard>
    );
  }

  if (loading)
    return (
      <SectionCard>
        <Text fontSize="13px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
          {t("settings.loading")}
        </Text>
      </SectionCard>
    );

  return (
    <>
      <SectionCard>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="24px">
          <SectionHeading
            title={t("settings.company.sectionTitle", "Company profile")}
            description={t("settings.company.sectionDescription", "Your company identity and billing contact details.")}
          />
          <Flex justify="space-between" align="center" gap="16px" flexWrap="wrap">
            <Flex align="center" gap="16px" minW={0}>
              <Avatar src={logoPreview} name={form.company_name} size="lg" bg="portal.active" color="portal.primary" />
              <Box minW={0}>
                <Heading as="h3" fontSize="15px" letterSpacing="-0.02em" color="portal.text" sx={{ marginBottom: "2px !important" }}>
                  {form.company_name || t("settings.company.displayCompanyFallback")}
                </Heading>
                {form.website && (
                  <Text fontSize="12px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                    {form.website}
                  </Text>
                )}
              </Box>
            </Flex>
            <Button {...secondaryBtnProps} leftIcon={<Icon as={FiEdit} />} onClick={onOpen} flexShrink={0}>
              {t("settings.company.headerButton")}
            </Button>
          </Flex>
        </SimpleGrid>
      </SectionCard>

      <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader>{t("settings.company.modalTitle")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto">
            <Stack spacing={5} maxW="lg">
              <FormControl>
                <FormLabel>{t("settings.company.fields.logo")}</FormLabel>
                <Flex align="center" gap={4}>
                  <Avatar src={logoPreview} name={form.company_name} size="lg" />
                  <Button as="label" {...secondaryBtnProps} leftIcon={<Icon as={FiUpload} />} cursor="pointer">
                    {t("settings.company.buttons.uploadLogo")}
                    <input type="file" accept="image/*" hidden onChange={handleLogoChange} />
                  </Button>
                </Flex>
              </FormControl>

              <FormControl>
                <FormLabel>{t("settings.company.fields.name")}</FormLabel>
                <Input name="company_name" value={form.company_name} onChange={handleChange} />
              </FormControl>

              <FormControl>
                <FormLabel>{t("settings.company.fields.managerName")}</FormLabel>
                <Input name="manager_name" value={form.manager_name} onChange={handleChange} />
              </FormControl>

              <FormControl>
                <FormLabel>{t("settings.company.fields.billingManagerName")}</FormLabel>
                <Input name="billing_manager_name" value={form.billing_manager_name} onChange={handleChange} />
              </FormControl>

              <FormControl>
                <FormLabel>{t("settings.company.fields.billingManagerEmail")}</FormLabel>
                <Input
                  name="billing_manager_email"
                  type="email"
                  value={form.billing_manager_email}
                  onChange={handleChange}
                />
              </FormControl>

              <FormControl>
                <FormLabel>{t("settings.company.fields.website")}</FormLabel>
                <Input name="website" value={form.website} onChange={handleChange} />
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} size="sm" h="31px" fontSize="12px">
              {t("settings.company.buttons.cancel")}
            </Button>
            <Button {...primaryBtnProps} leftIcon={<Icon as={FiSave} />} onClick={handleSave} isLoading={saving}>
              {t("settings.company.buttons.save")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

// Business Unit settings
const BusinessUnitSettingsTab = ({ unitId, isOpen: isOpenProp, onClose: onCloseProp }) => {
  const toast = useToast();
  const { t } = useTranslation("portal");
  const [formData, setFormData] = useState({
    unit_name: "",
    manager_name: "",
    billing_manager_name: "",
    billing_manager_email: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const disclosure = useDisclosure();
  const isOpen = typeof isOpenProp === "boolean" ? isOpenProp : disclosure.isOpen;
  const onClose = typeof onCloseProp === "function" ? onCloseProp : disclosure.onClose;

  // Fetch BU settings whenever the selected unit changes
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const url = `${process.env.REACT_APP_BASE_API_URL}/auth/business-unit${unitId ? `?business_unit_id=${unitId}` : ""}`;
        const res = await axios.get(url);
        setFormData((prev) => ({ ...prev, ...res.data }));
      } catch (err) {
        console.error("Failed to fetch business unit settings", err);
        toast({ title: t("settings.businessUnits.editModal.toasts.loadFailed"), status: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [unitId, toast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setSaving(true);
    try {
      const url = `${process.env.REACT_APP_BASE_API_URL}/auth/business-unit/${unitId || formData.id || formData.business_unit_id || formData.unit_code}`;
      await axios.patch(url, formData);
      toast({ title: t("settings.businessUnits.editModal.toasts.saveSuccess"), status: "success" });
      onClose();
    } catch (err) {
      console.error("Failed to save business unit settings", err);
      toast({ title: t("settings.businessUnits.editModal.toasts.saveFailed"), status: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("settings.businessUnits.editModal.title")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={5} maxW="lg">
              <FormControl isRequired>
                <FormLabel>{t("settings.businessUnits.editModal.fields.unitName")}</FormLabel>
                <Input name="unit_name" value={formData.unit_name} onChange={handleChange} />
              </FormControl>

              <FormControl>
                <FormLabel>{t("settings.businessUnits.editModal.fields.managerName")}</FormLabel>
                <Input name="manager_name" value={formData.manager_name} onChange={handleChange} />
              </FormControl>

              <FormControl>
                <FormLabel>{t("settings.businessUnits.editModal.fields.billingManagerName")}</FormLabel>
                <Input name="billing_manager_name" value={formData.billing_manager_name} onChange={handleChange} />
              </FormControl>

              <FormControl>
                <FormLabel>{t("settings.businessUnits.editModal.fields.billingManagerEmail")}</FormLabel>
                <Input
                  name="billing_manager_email"
                  type="email"
                  value={formData.billing_manager_email}
                  onChange={handleChange}
                />
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} size="sm" h="31px" fontSize="12px">
              {t("settings.businessUnits.editModal.buttons.cancel")}
            </Button>
            <Button {...primaryBtnProps} leftIcon={<Icon as={FiSave} />} onClick={handleSubmit} isLoading={saving}>
              {t("settings.businessUnits.editModal.buttons.save")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

// Resolve a user's display role label for a given BU context.
const resolveRoleLabel = (u, unitId) => {
  const buRole = Array.isArray(u.business_units)
    ? u.business_units.find((bu) => String(bu.id) === String(unitId))?.role
    : undefined;
  const role = buRole || u.role;
  if (role === "Admin" && buRole) {
    return "Admin";
  }
  const convertedRole = convertLegacyRole(role);
  return convertedRole === "COMPANY_ADMIN" ? "Company Admin" : convertedRole === "Admin" ? "Admin" : convertedRole;
};

// ----------------------------
// User management section
// ----------------------------
const UsersManagement = ({
  isCompanyAdmin,
  isBUAdmin,
  unitCode,
  unitId,
  unitName,
  companyBusinessUnits = [],
  adminBusinessUnits = [],
}) => {
  const toast = useToast();
  const { isOpen: isInviteOpen, onOpen: openInvite, onClose: closeInvite } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: openEdit, onClose: closeEdit } = useDisclosure();
  const { isOpen: isRemoveOpen, onOpen: openRemove, onClose: closeRemove } = useAlertDisclosure();


  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [selectedExistingUserId, setSelectedExistingUserId] = useState("");
  const [inviteData, setInviteData] = useState({
    email: "",
    first_name: "",
    last_name: "",
    role: unitId ? "Manager" : "Company Admin",
    business_unit_id: unitId || "",
    job_title: "",
    reporting_manager: "",
  });
  const [editingUser, setEditingUser] = useState(null);
  const [selectedBusinessUnitIds, setSelectedBusinessUnitIds] = useState([]);
  const [originalBusinessUnitIds, setOriginalBusinessUnitIds] = useState([]);
  const [userToRemove, setUserToRemove] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [emailLookupResult, setEmailLookupResult] = useState(null);
  const [isLookingUpEmail, setIsLookingUpEmail] = useState(false);
  const { t } = useTranslation("portal");

  // Keep invite data in sync with the currently selected BU (or lack thereof)
  useEffect(() => {
    setInviteData((prev) => ({
      ...prev,
      business_unit_id: unitId || "",
      role: unitId ? "Manager" : "Company Admin"
    }));
  }, [unitId]);

  const fetchUsers = async (buId) => {
    setLoadingUsers(true);
    try {
      const query = buId ? `?business_unit_id=${buId}` : "";
      const res = await axios.get(`${process.env.REACT_APP_BASE_API_URL}/auth/users${query}`);
      setUsers(res.data);
    } catch (err) {
      toast({ title: t("settings.users.toasts.fetchUsersFailed"), status: "error" });
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    fetchUsers(unitId);
  }, [unitId]);

  // Fetch all company users when the invite modal is opened in BU context.
  // This uses the dedicated /auth/company-users endpoint which:
  // - Is scoped at company level (ignores BU restrictions)
  // - Accepts COMPANY_ADMIN and Admin (BU admin) tokens
  const fetchCompanyUsers = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BASE_API_URL}/auth/company-users`);
      setCompanyUsers(res.data);
    } catch (err) {
      console.error("Failed to fetch company users", err);
    }
  };

  useEffect(() => {
    if (isInviteOpen && unitId) {
      fetchCompanyUsers();
    }
    if (!isInviteOpen) {
      setSelectedExistingUserId("");
    }
  }, [isInviteOpen, unitId]);

  const handleInvite = async () => {
    try {
      // Ensure we respect the current BU context
      const payload = { ...inviteData };
      if (!unitId) delete payload.business_unit_id;

      await axios.post(`${process.env.REACT_APP_BASE_API_URL}/auth/invite`, payload);
      toast({ title: t("settings.users.toasts.inviteSuccess"), status: "success" });
      closeInvite();
      setInviteData({ email: "", first_name: "", last_name: "", role: unitId ? "Manager" : "Company Admin", business_unit_id: unitId || "", job_title: "", reporting_manager: "" });
      setEmailLookupResult(null);
      fetchUsers(unitId);
    } catch (err) {
      toast({
        title: err.response?.data?.message || t("settings.users.toasts.inviteFailed"),
        status: "error",
      });
    }
  };

  const handleExistingUserSelect = (e) => {
    const value = e.target.value;
    setSelectedExistingUserId(value);

    if (!value) {
      return;
    }

    const selected = companyUsers.find((u) => String(u.id) === String(value));
    if (selected) {
      setInviteData((prev) => ({
        ...prev,
        email: selected.email || "",
        first_name: selected.first_name || "",
        last_name: selected.last_name || "",
        job_title: selected.job_title || "",
        reporting_manager: selected.reporting_manager || "",
      }));
    }
  };

  const handleEmailBlur = async () => {
    const email = inviteData.email?.trim();
    if (!email || !email.includes("@")) {
      setEmailLookupResult(null);
      return;
    }
    // Skip if already selected from existing user dropdown
    if (selectedExistingUserId) return;

    setIsLookingUpEmail(true);
    try {
      const res = await lookupUserByEmail(email);
      setEmailLookupResult(res);
      if (res?.exists) {
        setInviteData((prev) => ({
          ...prev,
          first_name: res.first_name || prev.first_name,
          last_name: res.last_name || prev.last_name,
        }));
      }
    } catch (err) {
      // Lookup failure is non-critical
      setEmailLookupResult(null);
    } finally {
      setIsLookingUpEmail(false);
    }
  };

  const handleUpdate = async () => {
    try {
      setIsUpdating(true);
      const editableBusinessUnits = isCompanyAdmin
        ? companyBusinessUnits
        : adminBusinessUnits.filter((bu) => convertLegacyRole(bu?.role) === "Admin");
      const editableBusinessUnitIds = new Set(
        editableBusinessUnits.map((bu) => String(bu.id))
      );
      const currentSelection = new Set(
        selectedBusinessUnitIds.map((id) => String(id))
      );
      const originalSelection = new Set(
        originalBusinessUnitIds.map((id) => String(id))
      );
      const addedBusinessUnits = [...currentSelection].filter(
        (id) => !originalSelection.has(id) && editableBusinessUnitIds.has(id)
      );
      const removedBusinessUnits = [...originalSelection].filter(
        (id) => !currentSelection.has(id) && editableBusinessUnitIds.has(id)
      );
      const url = `${process.env.REACT_APP_BASE_API_URL}/auth/users/${editingUser.id}${unitId ? `?business_unit_id=${unitId}` : ""}`;
      const updateRequest = axios.patch(url, editingUser);
      const inviteRequests = addedBusinessUnits.map((buId) =>
        axios.post(`${process.env.REACT_APP_BASE_API_URL}/auth/invite`, {
          email: editingUser.email,
          first_name: editingUser.first_name,
          last_name: editingUser.last_name,
          business_unit_id: buId,
        })
      );
      const removeRequests = removedBusinessUnits.map((buId) =>
        axios.delete(
          `${process.env.REACT_APP_BASE_API_URL}/auth/users/${editingUser.id}`,
          { params: { business_unit_id: buId } }
        )
      );
      await Promise.all([updateRequest, ...inviteRequests, ...removeRequests]);
      toast({ title: t("settings.users.toasts.updateSuccess"), status: "success" });
      closeEdit();
      fetchUsers(unitId);
    } catch (err) {
      toast({
        title: err.response?.data?.message || t("settings.users.toasts.updateFailed"),
        status: "error",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemoveUser = async () => {
    if (!userToRemove) return;

    setIsRemoving(true);
    try {
      const url = `${process.env.REACT_APP_BASE_API_URL}/auth/users/${userToRemove.id}`;
      const params = unitId ? { business_unit_id: unitId } : {};

      await axios.delete(url, { params });

      const title = unitId
        ? t("settings.users.toasts.removeSuccessBU")
        : t("settings.users.toasts.removeSuccessCompany");
      toast({
        title,
        status: "success",
      });

      closeRemove();
      setUserToRemove(null);
      fetchUsers(unitId);
    } catch (err) {
      toast({
        title: err.response?.data?.message || t("settings.users.toasts.removeFailed"),
        status: "error",
      });
    } finally {
      setIsRemoving(false);
    }
  };

  const openRemoveDialog = (user) => {
    setUserToRemove(user);
    openRemove();
  };

  // In company context (no specific BU selected), hide users that belong to any business unit
  const displayedUsers = unitId
    ? users
    : users.filter((u) => !Array.isArray(u.business_units) || u.business_units.length === 0);

  // For BU invites, offer a list of existing company users that are not yet in this BU
  const availableExistingUsers = (() => {
    if (!unitId || !Array.isArray(companyUsers) || companyUsers.length === 0) return [];
    const currentIds = new Set(users.map((u) => u.id));
    return companyUsers.filter((u) => !currentIds.has(u.id));
  })();

  const userColumns = [
    { key: "name", label: t("settings.users.table.columns.name"), width: "22%" },
    { key: "email", label: t("settings.users.table.columns.email") },
    { key: "role", label: t("settings.users.table.columns.role"), width: "16%" },
    ...(isCompanyAdmin
      ? [{ key: "bu", label: t("settings.users.table.columns.businessUnit"), width: "18%" }]
      : []),
    { key: "actions", label: t("settings.users.table.columns.actions"), width: "110px", align: "right" },
  ];

  return (
    <Panel
      mt="12px"
      title={t("settings.users.sectionTitle")}
      count={loadingUsers ? undefined : displayedUsers.length}
      actions={
        <Button {...primaryBtnProps} h="28px" px="10px" fontSize="11px" onClick={openInvite}>
          {t("settings.users.inviteButton")}
        </Button>
      }
    >
      <DataTable
        columns={userColumns}
        rows={displayedUsers}
        loading={loadingUsers}
        emptyState={
          <EmptyState
            icon={<FiUsers />}
            title={
              unitId
                ? t("settings.users.table.noUsersBU")
                : t("settings.users.table.noUsersCompany")
            }
            hint={t("settings.users.table.noUsersHint", "Invite a user to get started.")}
          />
        }
        renderRow={(u) => (
          <Tr key={u.id}>
            <Td fontWeight="700">
              {u.first_name} {u.last_name}
            </Td>
            <Td title={u.email}>{u.email}</Td>
            <Td>
              <StatusBadge label={resolveRoleLabel(u, unitId)} colorScheme="blue" />
            </Td>
            {isCompanyAdmin && (
              <Td>
                {Array.isArray(u.business_units) && u.business_units.length > 0
                  ? u.business_units
                      .map((bu) => bu.unit_code || bu.unit_name || `${bu.id}`)
                      .join(", ")
                  : "-"}
              </Td>
            )}
            <Td>
              <HStack justify="flex-end" spacing="6px">
                <MiniIconButton
                  aria-label={t("settings.users.table.edit")}
                  icon={<Icon as={FiEdit} />}
                  onClick={() => {
                    const buRole = Array.isArray(u.business_units)
                      ? u.business_units.find((bu) => String(bu.id) === String(unitId))?.role
                      : undefined;
                    const initialBusinessUnitIds = Array.isArray(u.business_units)
                      ? u.business_units.map((bu) => String(bu.id))
                      : [];
                    if (unitId && !initialBusinessUnitIds.includes(String(unitId))) {
                      initialBusinessUnitIds.push(String(unitId));
                    }
                    setEditingUser({ ...u, role: buRole || u.role });
                    setSelectedBusinessUnitIds(initialBusinessUnitIds);
                    setOriginalBusinessUnitIds(initialBusinessUnitIds);
                    openEdit();
                  }}
                />
                {(isCompanyAdmin || isBUAdmin) && (
                  <MiniIconButton
                    aria-label={t("settings.users.table.remove")}
                    icon={<Icon as={FiTrash2} />}
                    color="portal.danger"
                    _hover={{ bg: "danger.50", borderColor: "portal.danger", color: "portal.danger" }}
                    onClick={() => openRemoveDialog(u)}
                  />
                )}
              </HStack>
            </Td>
          </Tr>
        )}
      />

      {/* Invite Modal */}
      <Modal isOpen={isInviteOpen} onClose={closeInvite}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("settings.users.inviteModal.title")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              {/* When inviting within a BU, allow selecting an existing company user
                  so that details are pre-filled and do not need to be typed again. */}
              {unitId && (
                <FormControl>
                  <FormLabel>{t("settings.users.inviteModal.fields.existingUser")}</FormLabel>
                  <Select
                    placeholder={t(
                      "settings.users.inviteModal.placeholders.existingUserPlaceholder",
                      { defaultValue: "Select an existing user (optional)" }
                    )}
                    value={selectedExistingUserId}
                    onChange={handleExistingUserSelect}
                  >
                    {availableExistingUsers.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.first_name} {u.last_name} – {u.email}
                      </option>
                    ))}
                  </Select>
                  {availableExistingUsers.length === 0 && (
                    <FormHelperText>
                      {t(
                        "settings.users.inviteModal.helpers.noExistingUsersForBU",
                        {
                          defaultValue:
                            "No other company users are available to add to this business unit.",
                        }
                      )}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
              <FormControl isRequired>
                <FormLabel>{t("settings.users.inviteModal.fields.email")}</FormLabel>
                <Input
                  name="email"
                  type="email"
                  value={inviteData.email}
                  onChange={(e) => {
                    setInviteData({ ...inviteData, email: e.target.value });
                    setEmailLookupResult(null);
                  }}
                  onBlur={handleEmailBlur}
                />
                {isLookingUpEmail && (
                  <FormHelperText color="portal.muted">
                    {t("settings.users.inviteModal.helpers.lookingUp", { defaultValue: "Looking up user..." })}
                  </FormHelperText>
                )}
                {emailLookupResult?.exists && (
                  <Alert status="info" mt={2} borderRadius="md" fontSize="sm">
                    <AlertIcon />
                    <Box>
                      <AlertTitle fontSize="sm">
                        {t("settings.users.inviteModal.helpers.userExists", { defaultValue: "User already exists in this company" })}
                      </AlertTitle>
                      <AlertDescription fontSize="xs">
                        {t("settings.users.inviteModal.helpers.userExistsHint", {
                          defaultValue: "Name fields have been auto-filled. This user will be added to the selected Business Unit.",
                          name: `${emailLookupResult.first_name || ""} ${emailLookupResult.last_name || ""}`.trim(),
                        })}
                      </AlertDescription>
                    </Box>
                  </Alert>
                )}
              </FormControl>
              <FormControl isRequired>
                <FormLabel>{t("settings.users.inviteModal.fields.firstName")}</FormLabel>
                <Input
                  name="first_name"
                  value={inviteData.first_name}
                  onChange={(e) => setInviteData({ ...inviteData, first_name: e.target.value })}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>{t("settings.users.inviteModal.fields.lastName")}</FormLabel>
                <Input
                  name="last_name"
                  value={inviteData.last_name}
                  onChange={(e) => setInviteData({ ...inviteData, last_name: e.target.value })}
                />
              </FormControl>
              {/* Show BU only when we are inviting from a specific BU context */}
              {unitCode && (
                <FormControl>
                  <FormLabel>{t("settings.users.inviteModal.fields.businessUnitCode")}</FormLabel>
                  <Input value={unitCode} isDisabled />
                </FormControl>
              )}
              {/* Role selection - locked to Company Admin for company-level invites, selectable for BU invites */}
              <FormControl>
                <FormLabel>{t("settings.users.inviteModal.fields.role")}</FormLabel>
                {!unitId ? (
                  // Company-level invite - lock to Company Admin
                  <Input value={t("settings.users.inviteModal.labels.companyAdmin")} isDisabled />
                ) : (
                  // Business unit invite - allow role selection
                  <Select name="role" value={inviteData.role} onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}>
                    {ROLE_OPTIONS.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </Select>
                )}
              </FormControl>
              <FormControl>
                <FormLabel>{t("settings.users.inviteModal.fields.jobTitle")}</FormLabel>
                <Input
                  name="job_title"
                  value={inviteData.job_title}
                  onChange={(e) => setInviteData({ ...inviteData, job_title: e.target.value })}
                />
              </FormControl>
              <FormControl>
                <FormLabel>{t("settings.users.inviteModal.fields.reportingManager")}</FormLabel>
                <Input
                  name="reporting_manager"
                  value={inviteData.reporting_manager}
                  onChange={(e) => setInviteData({ ...inviteData, reporting_manager: e.target.value })}
                />
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeInvite} size="sm" h="31px" fontSize="12px">
              {t("settings.users.inviteModal.buttons.cancel")}
            </Button>
            <Button {...primaryBtnProps} onClick={handleInvite}>
              {t("settings.users.inviteModal.buttons.sendInvite")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={isEditOpen} onClose={closeEdit}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("settings.users.editModal.title")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {editingUser && (
              <Stack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>{t("settings.users.editModal.fields.firstName")}</FormLabel>
                  <Input
                    value={editingUser.first_name}
                    onChange={(e) => setEditingUser({ ...editingUser, first_name: e.target.value })}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>{t("settings.users.editModal.fields.lastName")}</FormLabel>
                  <Input
                    value={editingUser.last_name}
                    onChange={(e) => setEditingUser({ ...editingUser, last_name: e.target.value })}
                  />
                </FormControl>
                {(isCompanyAdmin || isBUAdmin) && (
                  <>
                    <FormControl>
                      <FormLabel>
                        {t("settings.users.editModal.fields.businessUnits", {
                          defaultValue: "Business units",
                        })}
                      </FormLabel>
                      {(() => {
                        const editableBusinessUnits = isCompanyAdmin
                          ? companyBusinessUnits
                          : adminBusinessUnits.filter((bu) => convertLegacyRole(bu?.role) === "Admin");
                        const editableIds = new Set(
                          editableBusinessUnits.map((bu) => String(bu.id))
                        );
                        const userBusinessUnits = Array.isArray(editingUser.business_units)
                          ? editingUser.business_units
                          : [];
                        const combinedUnitsMap = new Map();
                        [...userBusinessUnits, ...editableBusinessUnits].forEach((bu) => {
                          if (!bu || bu.id === undefined || bu.id === null) return;
                          const id = String(bu.id);
                          if (!combinedUnitsMap.has(id)) {
                            combinedUnitsMap.set(id, bu);
                          }
                        });
                        const combinedUnits = [...combinedUnitsMap.values()];
                        const sortedUnits = combinedUnits.sort((a, b) =>
                          String(a.unit_name || a.unit_code || a.unitCode || a.id).localeCompare(
                            String(b.unit_name || b.unit_code || b.unitCode || b.id)
                          )
                        );
                        if (sortedUnits.length === 0) {
                          return (
                            <Input
                              value={t("settings.users.editModal.labels.noBusinessUnits", {
                                defaultValue: "No business units available.",
                              })}
                              isDisabled
                            />
                          );
                        }
                        const selectedLabels = sortedUnits
                          .filter((bu) => selectedBusinessUnitIds.includes(String(bu.id)))
                          .map((bu) => bu.unit_name || bu.unit_code || bu.unitCode || `${bu.id}`);
                        const buttonLabel =
                          selectedLabels.length === 0
                            ? t("settings.users.editModal.labels.selectBusinessUnits", {
                                defaultValue: "Select business units",
                              })
                            : selectedLabels.length <= 2
                              ? selectedLabels.join(", ")
                              : `${selectedLabels.slice(0, 2).join(", ")} +${selectedLabels.length - 2}`;
                        return (
                          <Menu closeOnSelect={false}>
                            <MenuButton
                              as={Button}
                              rightIcon={<Icon as={FiChevronDown} />}
                              variant="secondary"
                              size="sm"
                              h="31px"
                              fontSize="12px"
                              w="full"
                              textAlign="left"
                              justifyContent="space-between"
                            >
                              {buttonLabel}
                            </MenuButton>
                            <MenuList maxH="240px" overflowY="auto">
                              <MenuOptionGroup
                                type="checkbox"
                                value={selectedBusinessUnitIds}
                                onChange={(value) =>
                                  setSelectedBusinessUnitIds(
                                    Array.isArray(value) ? value : [value]
                                  )
                                }
                              >
                                {sortedUnits.map((bu) => {
                                  const id = String(bu.id);
                                  const label = bu.unit_name || bu.unit_code || bu.unitCode || `${bu.id}`;
                                  return (
                                    <MenuItemOption
                                      key={id}
                                      value={id}
                                      isDisabled={!editableIds.has(id)}
                                    >
                                      {label}
                                    </MenuItemOption>
                                  );
                                })}
                              </MenuOptionGroup>
                            </MenuList>
                          </Menu>
                        );
                      })()}
                      {isBUAdmin && !isCompanyAdmin && (
                        <FormHelperText>
                          {t("settings.users.editModal.helpers.buAdminLimited", {
                            defaultValue:
                              "You can only add or remove users in business units you administer.",
                          })}
                        </FormHelperText>
                      )}
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t("settings.users.editModal.fields.role")}</FormLabel>
                      {(() => {
                        // For business unit context, don't convert "Admin" to "Company Admin"
                        let convertedRole = editingUser?.role;
                        if (editingUser?.role === "Admin" && unitId) {
                          // This is a BU admin, keep it as "Admin"
                          convertedRole = "Admin";
                        } else {
                          convertedRole = convertLegacyRole(editingUser?.role);
                        }
                        const currentRolePretty = convertedRole === "COMPANY_ADMIN" ? "Company Admin" : convertedRole;
                        const hasCurrent = ROLE_OPTIONS.some((opt) => opt.toLowerCase() === currentRolePretty.toLowerCase());
                        return (
                          <Select
                            value={currentRolePretty}
                            onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                          >
                            {!hasCurrent && currentRolePretty && (
                              <option value={currentRolePretty}>{currentRolePretty}</option>
                            )}
                            {ROLE_OPTIONS.map((r) => (
                              <option key={r} value={r}>{r}</option>
                            ))}
                          </Select>
                        );
                      })()}
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t("settings.users.editModal.fields.jobTitle")}</FormLabel>
                      <Input
                        value={editingUser.job_title || ""}
                        onChange={(e) => setEditingUser({ ...editingUser, job_title: e.target.value })}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t("settings.users.editModal.fields.reportingManager")}</FormLabel>
                      <Input
                        value={editingUser.reporting_manager || ""}
                        onChange={(e) => setEditingUser({ ...editingUser, reporting_manager: e.target.value })}
                      />
                    </FormControl>
                  </>
                )}
              </Stack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeEdit} size="sm" h="31px" fontSize="12px">
              {t("settings.users.editModal.buttons.cancel")}
            </Button>
            {(isCompanyAdmin || isBUAdmin) && (
              <Button
                {...secondaryBtnProps}
                color="portal.danger"
                borderColor="portal.danger"
                _hover={{ bg: "danger.50", borderColor: "portal.danger" }}
                leftIcon={<Icon as={FiTrash2} />}
                onClick={() => {
                  closeEdit();
                  openRemoveDialog(editingUser);
                }}
                mr={3}
              >
                {t("settings.users.editModal.buttons.removeUser")}
              </Button>
            )}
            <Button {...primaryBtnProps} onClick={handleUpdate} isLoading={isUpdating}>
              {t("settings.users.editModal.buttons.save")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Remove User Confirmation Dialog */}
      <AlertDialog isOpen={isRemoveOpen} onClose={closeRemove} leastDestructiveRef={undefined}>
        <AlertDialogOverlay>
          <AlertDialogContent maxW="md">
            <AlertDialogHeader fontSize="lg" fontWeight="semibold" color="portal.text">
              {t("settings.users.removeDialog.title")}
            </AlertDialogHeader>
            <AlertDialogBody>
              {userToRemove && (
                <Stack spacing={4}>
                  <Box>
                    <Text fontSize="md" color="portal.text" mb={2}>
                      {t("settings.users.removeDialog.confirmQuestion")}
                    </Text>
                    <Box p={3} bg="portal.soft" borderRadius="md" borderWidth="1px" borderColor="portal.line">
                      <Flex align="center" gap={3}>
                        <Avatar
                          size="sm"
                          name={`${userToRemove.first_name} ${userToRemove.last_name}`}
                          bg="portal.primary"
                        />
                        <Box>
                          <Text fontWeight="medium" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                            {userToRemove.first_name} {userToRemove.last_name}
                          </Text>
                          <Text fontSize="sm" color="portal.muted" sx={{ marginBottom: "4px !important" }}>
                            {userToRemove.email}
                          </Text>
                          <StatusBadge
                            colorScheme="blue"
                            label={(() => {
                              const buRole = Array.isArray(userToRemove.business_units)
                                ? userToRemove.business_units.find((bu) => String(bu.id) === String(unitId))?.role
                                : undefined;
                              const role = buRole || userToRemove.role;
                              const convertedRole = convertLegacyRole(role);
                              if (convertedRole === "COMPANY_ADMIN") {
                                return t("settings.users.removeDialog.roleBadge.companyAdmin");
                              }
                              if (convertedRole === "Admin") {
                                return t("settings.users.removeDialog.roleBadge.admin");
                              }
                              return convertedRole;
                            })()}
                          />
                        </Box>
                      </Flex>
                    </Box>
                  </Box>

                  <Divider borderColor="portal.line" />

                  {!unitId ? (
                    <Alert status="error" variant="left-accent" borderRadius="md">
                      <AlertIcon />
                      <Box>
                        <AlertTitle fontSize="sm" fontWeight="semibold">
                          {t("settings.users.removeDialog.permanentDeletionTitle")}
                        </AlertTitle>
                        <AlertDescription fontSize="sm">
                          {t("settings.users.removeDialog.permanentDeletionDescription")}
                        </AlertDescription>
                      </Box>
                    </Alert>
                  ) : (
                    <Alert status="warning" variant="left-accent" borderRadius="md">
                      <AlertIcon />
                      <Box>
                        <AlertTitle fontSize="sm" fontWeight="semibold">
                          {t("settings.users.removeDialog.buRemovalTitle")}
                        </AlertTitle>
                        <AlertDescription fontSize="sm">
                          {t("settings.users.removeDialog.buRemovalDescription")}
                        </AlertDescription>
                      </Box>
                    </Alert>
                  )}
                </Stack>
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                onClick={closeRemove}
                isDisabled={isRemoving}
                variant="ghost"
                size="sm"
                h="31px"
                fontSize="12px"
              >
                {t("settings.users.removeDialog.buttons.cancel")}
              </Button>
              <Button
                {...primaryBtnProps}
                bg="portal.danger"
                borderColor="portal.danger"
                _hover={{ bg: "#a82e38", borderColor: "#a82e38" }}
                onClick={handleRemoveUser}
                ml={3}
                isLoading={isRemoving}
                loadingText={t("settings.users.removeDialog.buttons.removing")}
              >
                {unitId
                  ? t("settings.users.removeDialog.buttons.removeFromBU")
                  : t("settings.users.removeDialog.buttons.deleteUser")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Panel>
  );
};

// ── Partners Email Config Tab ──
const PartnersEmailConfigTab = () => {
  const toast = useToast();
  const { t } = useTranslation("portal");
  const { selectedBusinessUnitId } = useBusinessUnit();

  const [emailConfig, setEmailConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const { isOpen: isConfigOpen, onOpen: openConfig, onClose: closeConfig } = useDisclosure();
  const [configForm, setConfigForm] = useState({
    usePlatformSender: true,
    senderEmail: "",
    smtpHost: "",
    smtpPort: "",
    authProtocol: "starttls",
    senderPassword: "",
    fromLocalPart: "",
  });
  const [isSubmittingConfig, setIsSubmittingConfig] = useState(false);

  const fetchEmailConfig = React.useCallback(async () => {
    setLoadingConfig(true);
    try {
      const { getEmailConfig } = await import("../../api/partnersApi");
      const res = await getEmailConfig({ buId: selectedBusinessUnitId || undefined });
      setEmailConfig(res);
    } catch {
      setEmailConfig(null);
    } finally {
      setLoadingConfig(false);
    }
  }, [selectedBusinessUnitId]);

  useEffect(() => {
    fetchEmailConfig();
  }, [fetchEmailConfig]);

  const openConfigModal = () => {
    setConfigForm({
      usePlatformSender: emailConfig?.usePlatformSender ?? true,
      senderEmail: emailConfig?.senderEmail || "",
      smtpHost: emailConfig?.smtpHost || "",
      smtpPort: emailConfig?.smtpPort || "",
      authProtocol: emailConfig?.authProtocol || "starttls",
      senderPassword: "",
      fromLocalPart: emailConfig?.fromLocalPart || "",
    });
    openConfig();
  };

  const handleConfigChange = (e) => {
    const { name, value } = e.target;
    setConfigForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleConfigSubmit = async () => {
    setIsSubmittingConfig(true);
    try {
      const { updateEmailConfig } = await import("../../api/partnersApi");
      const payload = configForm.usePlatformSender
        ? {
            usePlatformSender: true,
            fromLocalPart: configForm.fromLocalPart.trim() || undefined,
          }
        : {
            usePlatformSender: false,
            senderEmail: configForm.senderEmail.trim(),
            smtpHost: configForm.smtpHost.trim() || undefined,
            smtpPort: configForm.smtpPort ? Number(configForm.smtpPort) : undefined,
            authProtocol: configForm.authProtocol,
            senderPassword: configForm.senderPassword.trim() || undefined,
          };
      await updateEmailConfig(payload, { buId: selectedBusinessUnitId || undefined });
      toast({
        title: t("partners.communications.toasts.configUpdated", "Email configuration updated"),
        status: "success",
      });
      closeConfig();
      fetchEmailConfig();
    } catch (e) {
      toast({
        title: t("partners.communications.toasts.configFailedTitle", "Update failed"),
        description: e?.response?.data?.message || t("partners.communications.toasts.configFailedDescription", "Could not update email configuration."),
        status: "error",
      });
    } finally {
      setIsSubmittingConfig(false);
    }
  };

  const handleVerify = async () => {
    try {
      const { verifyEmailConfig } = await import("../../api/partnersApi");
      await verifyEmailConfig({ buId: selectedBusinessUnitId || undefined });
      toast({
        title: t("partners.communications.toasts.verifySuccess", "Test email sent — configuration verified"),
        status: "success",
      });
      fetchEmailConfig();
    } catch (e) {
      toast({
        title: t("partners.communications.toasts.verifyFailed", "Verification failed"),
        description: e?.response?.data?.detail || "",
        status: "error",
      });
    }
  };

  return (
    <SectionCard
      title={t("partners.communications.emailConfig.title", "Email Configuration")}
      actions={
        <HStack spacing="8px">
          {emailConfig && !emailConfig.isVerified && (
            <Button {...secondaryBtnProps} h="28px" px="10px" fontSize="11px" onClick={handleVerify}>
              {t("partners.communications.emailConfig.verifyButton", "Verify")}
            </Button>
          )}
          <Button {...primaryBtnProps} h="28px" px="10px" fontSize="11px" onClick={openConfigModal}>
            {t("partners.communications.emailConfig.configureButton", "Configure")}
          </Button>
        </HStack>
      }
    >
      <Heading
        as="h3"
        fontSize="11px"
        fontWeight="800"
        textTransform="uppercase"
        letterSpacing="0.055em"
        color="portal.muted"
        sx={{ marginBottom: "6px !important" }}
      >
        {t("partners.communications.emailConfig.smtpSettings", "SMTP Settings")}
      </Heading>
      <Text fontSize="12px" color="portal.muted" mb="16px" sx={{ marginBottom: "16px !important" }}>
        {t("settings.partnersEmail.description", "Configure the SMTP email settings used by ASAP Partners to send communications from this business unit.")}
      </Text>

      {loadingConfig ? (
        <Flex align="center" justify="center" py={4}>
          <Text color="portal.muted" sx={{ marginBottom: "0 !important" }}>{t("common.loading", "Loading...")}</Text>
        </Flex>
      ) : !emailConfig ? (
        <EmptyState
          icon={<FiMail />}
          title={t("partners.communications.emailConfig.notConfiguredTitle", "Not configured")}
          hint={t("partners.communications.emailConfig.notConfigured", "Email sending is not configured yet. Click Configure to set up.")}
        />
      ) : (
        <Box>
          <ConfigRow label={t("partners.communications.emailConfig.senderMode", "Sender Mode")}>
            <StatusBadge
              colorScheme={emailConfig.usePlatformSender ? "blue" : "gray"}
              label={
                emailConfig.usePlatformSender
                  ? t("partners.communications.emailConfig.senderModePlatform", "ASAP HUB default sender")
                  : t("partners.communications.emailConfig.senderModeOwn", "Own email server")
              }
            />
          </ConfigRow>
          {emailConfig.usePlatformSender && (
            <ConfigRow label={t("partners.communications.emailConfig.senderAddress", "Sender Address")}>
              {emailConfig.fromLocalPart
                ? `${emailConfig.fromLocalPart}@${emailConfig.fromDomain || "notifications.asap-cv.fr"}`
                : t("partners.communications.emailConfig.senderAddressAuto", "Auto-generated on first send")}
            </ConfigRow>
          )}
          {!emailConfig.usePlatformSender && (
            <>
              <ConfigRow label={t("partners.communications.emailConfig.senderEmail", "Sender Email")}>
                {emailConfig.senderEmail || "-"}
              </ConfigRow>
              <ConfigRow label={t("partners.communications.emailConfig.smtpHost", "SMTP Host")}>
                {emailConfig.smtpHost || "-"}
              </ConfigRow>
              <ConfigRow label={t("partners.communications.emailConfig.smtpPort", "SMTP Port")}>
                {emailConfig.smtpPort || "-"}
              </ConfigRow>
              <ConfigRow label={t("partners.communications.emailConfig.authProtocol", "Auth Protocol")}>
                {emailConfig.authProtocol || "-"}
              </ConfigRow>
            </>
          )}
          <ConfigRow label={t("partners.communications.emailConfig.verified", "Verified")}>
            <StatusBadge
              colorScheme={emailConfig.isVerified ? "green" : "orange"}
              label={
                emailConfig.isVerified
                  ? t("partners.communications.emailConfig.verifiedYes", "Verified")
                  : t("partners.communications.emailConfig.verifiedNo", "Not Verified")
              }
            />
          </ConfigRow>
        </Box>
      )}

      {/* Email Config Modal */}
      <Modal isOpen={isConfigOpen} onClose={closeConfig} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t("partners.communications.emailConfig.modalTitle", "Configure Email Settings")}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <FormControl display="flex" alignItems="center" justifyContent="space-between" gap={4}>
                <Box flex="1">
                  <FormLabel htmlFor="use-platform-sender" mb={0}>
                    {t("partners.communications.emailConfig.form.usePlatformSender", "Use ASAP HUB default sender")}
                  </FormLabel>
                  <FormHelperText mt={1}>
                    {t(
                      "partners.communications.emailConfig.form.usePlatformSenderHelp",
                      "Send emails from the shared no-reply@asap-hub.ai address — no SMTP setup required.",
                    )}
                  </FormHelperText>
                </Box>
                <Switch
                  id="use-platform-sender"
                  data-testid="use-platform-sender-switch"
                  isChecked={configForm.usePlatformSender}
                  onChange={(e) =>
                    setConfigForm((prev) => ({ ...prev, usePlatformSender: e.target.checked }))
                  }
                />
              </FormControl>

              {configForm.usePlatformSender && (
                <FormControl>
                  <FormLabel>{t("partners.communications.emailConfig.form.fromLocalPart", "Sender address")}</FormLabel>
                  <InputGroup>
                    <Input
                      name="fromLocalPart"
                      data-testid="from-local-part-input"
                      value={configForm.fromLocalPart}
                      onChange={handleConfigChange}
                      placeholder={t("partners.communications.emailConfig.form.fromLocalPartPlaceholder", "yourcompany")}
                    />
                    <InputRightAddon>@{emailConfig?.fromDomain || "notifications.asap-cv.fr"}</InputRightAddon>
                  </InputGroup>
                  <FormHelperText mt={1}>
                    {t(
                      "partners.communications.emailConfig.form.fromLocalPartHelp",
                      "The address your mission emails are sent from. Leave blank to auto-generate from your company name.",
                    )}
                  </FormHelperText>
                </FormControl>
              )}

              {!configForm.usePlatformSender && (
                <>
                  <FormControl isRequired>
                    <FormLabel>{t("partners.communications.emailConfig.form.senderEmail", "Sender Email")}</FormLabel>
                    <Input name="senderEmail" type="email" value={configForm.senderEmail} onChange={handleConfigChange} placeholder="noreply@yourcompany.com" />
                  </FormControl>
                  <FormControl>
                    <FormLabel>{t("partners.communications.emailConfig.form.senderPassword", "Password / App Password")}</FormLabel>
                    <Input name="senderPassword" type="password" value={configForm.senderPassword} onChange={handleConfigChange}
                      placeholder={t("partners.communications.emailConfig.form.senderPasswordPlaceholder", "Leave blank to keep current")} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>{t("partners.communications.emailConfig.form.smtpHost", "SMTP Host")}</FormLabel>
                    <Input name="smtpHost" value={configForm.smtpHost} onChange={handleConfigChange} placeholder="smtp.gmail.com" />
                  </FormControl>
                  <FormControl>
                    <FormLabel>{t("partners.communications.emailConfig.form.smtpPort", "SMTP Port")}</FormLabel>
                    <Input name="smtpPort" type="number" value={configForm.smtpPort} onChange={handleConfigChange} placeholder="587" />
                  </FormControl>
                  <FormControl>
                    <FormLabel>{t("partners.communications.emailConfig.form.authProtocol", "Auth Protocol")}</FormLabel>
                    <Select name="authProtocol" value={configForm.authProtocol} onChange={handleConfigChange}>
                      <option value="starttls">{t("partners.communications.emailConfig.form.authStarttls", "STARTTLS")}</option>
                      <option value="ssl">{t("partners.communications.emailConfig.form.authSsl", "SSL")}</option>
                      <option value="oauth2">{t("partners.communications.emailConfig.form.authOauth2", "OAuth2")}</option>
                    </Select>
                  </FormControl>
                </>
              )}
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeConfig} size="sm" h="31px" fontSize="12px">
              {t("partners.communications.emailConfig.form.cancel", "Cancel")}
            </Button>
            <Button
              {...primaryBtnProps}
              onClick={handleConfigSubmit}
              isLoading={isSubmittingConfig}
              isDisabled={!configForm.usePlatformSender && !configForm.senderEmail.trim()}
            >
              {t("partners.communications.emailConfig.form.save", "Save Configuration")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </SectionCard>
  );
};

const Settings = () => {
  const { user } = useAuth();
  const { selectedBusinessUnit, currentRole, businessUnits, effectiveRole, isLoaded } = useBusinessUnit();
  const { t } = useTranslation("portal");

  // For company admin permissions, check the base user role, not the effective role
  // This allows COMPANY_ADMIN users to manage business units even when acting as BU admin
  const isCompanyAdminUser = isCompanyAdmin(convertLegacyRoleWithContext(user?.role, businessUnits.length > 0));
  const isBUAdminUser = isBUAdmin(effectiveRole);


  const toast = useToast();
  const [units, setUnits] = useState([]);
  const [selectedUnitId, setSelectedUnitId] = useState(selectedBusinessUnit?.id || "");

  // Modal state for new BU
  const [isOpen, setIsOpen] = useState(false);
  const [newBU, setNewBU] = useState({
    unit_name: "",
    manager_name: "",
    billing_manager_name: "",
    billing_manager_email: "",
  });
  const { isOpen: isBUEditOpen, onOpen: openBUEdit, onClose: closeBUEdit } = useDisclosure();

  const openModal = () => setIsOpen(true);
  const closeModal = () => {
    setIsOpen(false);
    setNewBU({
      unit_name: "",
      manager_name: "",
      billing_manager_name: "",
      billing_manager_email: "",
    });
  };

  const handleNewBUChange = (e) => {
    const { name, value } = e.target;
    setNewBU((prev) => ({ ...prev, [name]: value }));
  };

  const createBusinessUnit = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_BASE_API_URL}/auth/business-unit`, newBU);
      toast({ title: t("settings.businessUnits.toasts.created", "Business unit created"), status: "success" });
      closeModal();
      // refresh units list
      const res = await axios.get(`${process.env.REACT_APP_BASE_API_URL}/auth/business-units`);
      setUnits(res.data);
    } catch (err) {
      toast({ title: t("settings.businessUnits.toasts.createFailed", "Creation failed"), status: "error" });
    }
  };

  useEffect(() => {
    // Always fetch business units when the page loads (user has already passed permission check)
    if (isLoaded) {
      axios
        .get(`${process.env.REACT_APP_BASE_API_URL}/auth/business-units`)
        .then((res) => {
          setUnits(res.data);
          if (res.data.length && !selectedUnitId) setSelectedUnitId(res.data[0].id);
        })
        .catch((err) => console.error("Failed to fetch units", err));
    }
  }, [isLoaded, selectedUnitId]);

  // selectedBusinessUnit already obtained above

  if (!user) return null;

  // Show loading state while BusinessUnitContext is loading
  if (!isLoaded) {
    return (
      <Box mt="-24px" mx="-24px" mb="-24px" minH="100%">
        <PageHeader moduleCode="ST" eyebrow={t("settings.eyebrow", "Administration")} title={t("settings.pageTitle")} />
        <Box p="20px" maxW="1100px">
          <SectionCard>
            <Text fontSize="13px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
              {t("settings.loading")}
            </Text>
          </SectionCard>
        </Box>
      </Box>
    );
  }

  // Ensure only admins can access settings
  if (!isCompanyAdminUser && !isBUAdminUser) {
    return (
      <Box mt="-24px" mx="-24px" mb="-24px" minH="100%">
        <PageHeader moduleCode="ST" eyebrow={t("settings.eyebrow", "Administration")} title={t("settings.pageTitle")} />
        <Box p="20px" maxW="1100px">
          <SectionCard>
            <EmptyState
              icon={<FiBriefcase />}
              title={t("settings.noPermissionTitle", "No access")}
              hint={t("settings.noPermission")}
            />
          </SectionCard>
        </Box>
      </Box>
    );
  }

  const newBUButton = isCompanyAdminUser ? (
    <Button {...primaryBtnProps} h="28px" px="10px" fontSize="11px" onClick={openModal}>
      {t("settings.businessUnits.newButton")}
    </Button>
  ) : null;

  const buColumns = [
    { key: "name", label: t("settings.businessUnits.table.columns.name") },
    { key: "code", label: t("settings.businessUnits.table.columns.code"), width: "18%" },
    { key: "manager", label: t("settings.businessUnits.table.columns.manager"), width: "22%" },
    { key: "actions", label: t("settings.businessUnits.table.columns.actions"), width: "90px", align: "right" },
  ];

  return (
    <Box mt="-24px" mx="-24px" mb="-24px" minH="100%">
      <PageHeader
        moduleCode="ST"
        eyebrow={t("settings.eyebrow", "Administration")}
        title={t("settings.pageTitle")}
      />

      <Box p="20px" maxW="1100px">
        <Tabs colorScheme="teal" variant="enclosed">
          <TabList>
            {isCompanyAdminUser && <Tab>{t("settings.tabs.company")}</Tab>}
            <Tab>{t("settings.tabs.businessUnit")}</Tab>
            {selectedBusinessUnit?.has_partners && <Tab>{t("settings.tabs.partnersEmail", "Partners Email")}</Tab>}
          </TabList>

          <TabPanels>
            {isCompanyAdminUser && (
              <TabPanel px={0} pt="16px">
                <Stack spacing="12px">
                  <CompanySettingsTab />
                  {/* Users table for entire company - only visible to company admins */}
                  <UsersManagement
                    isCompanyAdmin={isCompanyAdminUser}
                    isBUAdmin={isBUAdminUser}
                    unitCode={null}
                    unitId={null}
                    companyBusinessUnits={units}
                    adminBusinessUnits={businessUnits}
                  />
                </Stack>
              </TabPanel>
            )}

            <TabPanel px={0} pt="16px">
              {/* Business Unit context */}
              {units.length === 0 ? (
                <SectionCard>
                  <EmptyState
                    icon={<FiBriefcase />}
                    title={t("settings.businessUnits.noUnits")}
                    hint={t("settings.businessUnits.noUnitsHint", "Create a business unit to organize your teams.")}
                  />
                  {isCompanyAdminUser && (
                    <Flex justify="center" pb="20px">
                      {newBUButton}
                    </Flex>
                  )}
                </SectionCard>
              ) : (
                <Stack spacing="12px">
                  <Panel
                    title={t("settings.businessUnits.listTitle")}
                    count={units.length}
                    actions={newBUButton}
                  >
                    <DataTable
                      columns={buColumns}
                      rows={units}
                      emptyState={
                        <EmptyState
                          icon={<FiBriefcase />}
                          title={t("settings.businessUnits.table.noUnits")}
                        />
                      }
                      renderRow={(u) => (
                        <Tr
                          key={u.id}
                          bg={u.id === selectedUnitId ? "portal.active" : "transparent"}
                          cursor="pointer"
                          onClick={() => setSelectedUnitId(u.id)}
                        >
                          <Td fontWeight="700">{u.unit_name || "-"}</Td>
                          <Td>
                            <Box
                              as="span"
                              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                              color="portal.primary"
                              fontWeight="800"
                            >
                              {u.unit_code || "-"}
                            </Box>
                          </Td>
                          <Td>{u.manager_name || "-"}</Td>
                          <Td>
                            <HStack justify="flex-end" spacing="6px">
                              <MiniIconButton
                                aria-label={t("settings.businessUnits.table.edit")}
                                icon={<Icon as={FiEdit} />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedUnitId(u.id);
                                  openBUEdit();
                                }}
                              />
                            </HStack>
                          </Td>
                        </Tr>
                      )}
                    />
                  </Panel>

                  {/* BU settings & users */}
                  {/* Determine selected unit code for user filtering */}
                  {(() => {
                    const selectedObj = units.find((u) => u.id === selectedUnitId);
                    const codeForFilter = selectedObj?.unit_code || selectedObj?.unitCode || "";
                    return (
                      <>
                        {!isCompanyAdminUser && isBUAdminUser && (
                          <Flex justify="flex-end">
                            <Button {...secondaryBtnProps} leftIcon={<Icon as={FiEdit} />} onClick={openBUEdit}>
                              {t("settings.businessUnits.editButtonForBUAdmin")}
                            </Button>
                          </Flex>
                        )}

                        <BusinessUnitSettingsTab
                          unitId={isCompanyAdminUser ? selectedUnitId : selectedBusinessUnit?.id}
                          isOpen={isBUEditOpen}
                          onClose={closeBUEdit}
                        />
                        <UsersManagement
                          isCompanyAdmin={isCompanyAdminUser}
                          isBUAdmin={isBUAdminUser}
                          unitCode={isCompanyAdminUser ? codeForFilter : selectedBusinessUnit?.unit_code}
                          unitId={isCompanyAdminUser ? selectedUnitId : selectedBusinessUnit?.id}
                          unitName={isCompanyAdminUser ? selectedObj?.unit_name || selectedObj?.unitName : selectedBusinessUnit?.unit_name}
                          companyBusinessUnits={units}
                          adminBusinessUnits={businessUnits}
                        />
                      </>
                    );
                  })()}
                </Stack>
              )}
            </TabPanel>

            {selectedBusinessUnit?.has_partners && (
              <TabPanel px={0} pt="16px">
                <PartnersEmailConfigTab />
              </TabPanel>
            )}
          </TabPanels>
        </Tabs>
      </Box>

      {/* Create BU modal */}
      <Modal isOpen={isOpen} onClose={closeModal} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("settings.businessUnits.createModal.title")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <FormControl isRequired>
                <FormLabel>{t("settings.businessUnits.createModal.fields.unitName")}</FormLabel>
                <Input name="unit_name" value={newBU.unit_name} onChange={handleNewBUChange} />
              </FormControl>
              <FormControl>
                <FormLabel>{t("settings.businessUnits.createModal.fields.managerName")}</FormLabel>
                <Input name="manager_name" value={newBU.manager_name} onChange={handleNewBUChange} />
              </FormControl>
              <FormControl>
                <FormLabel>{t("settings.businessUnits.createModal.fields.billingManagerName")}</FormLabel>
                <Input name="billing_manager_name" value={newBU.billing_manager_name} onChange={handleNewBUChange} />
              </FormControl>
              <FormControl>
                <FormLabel>{t("settings.businessUnits.createModal.fields.billingManagerEmail")}</FormLabel>
                <Input
                  name="billing_manager_email"
                  type="email"
                  value={newBU.billing_manager_email}
                  onChange={handleNewBUChange}
                />
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={closeModal} mr={3} variant="ghost" size="sm" h="31px" fontSize="12px">
              {t("settings.businessUnits.createModal.buttons.cancel")}
            </Button>
            <Button {...primaryBtnProps} onClick={createBusinessUnit}>
              {t("settings.businessUnits.createModal.buttons.create")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Settings;
